/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LicensingModule
 */

import {
    Component,
    EventEmitter,
    Inject,
    OnDestroy,
    OnInit,
    Output,
    Type,
} from '@angular/core';

import {
    ALBServicesConnectivityStatus,
    ALBServicesRegistrationStatus,
    LicenseTierType,
} from 'generated-types';

import {
    combineLatest,
    Subject,
    Subscription,
} from 'rxjs';

import { CportalService } from 'ajs/modules/cportal';
import { DialogService } from 'ng/modules/core/services/dialog.service';
import { IPromise } from 'angular';
import { L10nService } from '@vmw/ngx-vip';
import { NotificationService } from 'ng/modules/core/services/notification.service';
import { Store } from '@ngrx/store';
import { SystemConfig } from 'ajs/modules/system';
import { takeUntil } from 'rxjs/operators';
import * as SystemInfoActions from 'ng/root-store/system-info/system-info.actions';
import * as SystemInfoSelectors from 'ng/root-store/system-info/system-info.selectors';

import './licensing-get-started.component.less';
import { ConnectToOnPremModalComponent } from '../connect-on-prem-modal';
import { LicenseTierSwitchNotificationComponent } from '../license-tier-switch-notification';
import { LicensingPageStore } from '../licensing-page.store';
import * as l10n from './licensing-get-started.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const CONNECT_TO_ON_PREM_MODAL = 'CONNECT_TO_ON_PREM_MODAL';

/**
 * @description Component to display GET STARTED section with Cloud Licensing and On-prem License Hub options.
 * @author Shubam Bindal, Deepak Chaudhary
 */
@Component({
    selector: 'licensing-get-started',
    templateUrl: './licensing-get-started.component.html',
})
export class LicensingGetStartedComponent implements OnInit, OnDestroy {
    /**
     * Emit data containing the selected licensing type.
     */
    @Output()
    public onConnectToCloudLicensing = new EventEmitter<{ licensingType: string }>();

    /**
     * Emit data containing the selected licensing type.
     */
    @Output()
    public onUseOnPremLicensingService = new EventEmitter<{ licensingType: string }>();

    /**
     * Emit event to request opening the license tier change modal.
     */
    @Output()
    public onLicenseTierChangeRequest = new EventEmitter<void>();

    /**
     * Emit error to parent component for display.
     */
    @Output()
    public onError = new EventEmitter<string>();

    public readonly tierInUse$ = this.licensingPageStore.tierInUse$;

    public error!: string;

    /**
     * Name of existing controller.
     */
    public controllerName = '';

    /**
     * Licensing types enum for template usage.
     */
    public readonly licensingTypes = {
        ENTERPRISE_WITH_CLOUD_SERVICES: LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES,
        ENTERPRISE: LicenseTierType.ENTERPRISE,
    };

    public readonly l10nKeys = l10nKeys;

    public busy = false;

    /**
     * Flag to track if license tier change API call is in progress.
     */
    public isTierChangeInProgress!: boolean;

    public selectedTier: LicenseTierType;

    /**
     * Used to check if controller is connected with the Cloud Console (Pulse Portal).
     * Wizard state
     */
    private aviCloudConnected = false;

    private licenseTierSubscription: Subscription;

    /**
     * Previous connectivity status to detect transitions.
     */
    private previousConnectivityStatus?: ALBServicesConnectivityStatus;

    /**
     * Flag to track if modal has been opened on first connect transition.
     */
    private hasOpenedModalOnConnect = false;

    /**
     * Subject used to unsubscribe observables.
     */
    private readonly ngDestroyed$ = new Subject();

    constructor(
        private readonly cPortalService: CportalService,
        private readonly dialogService: DialogService,
        private readonly l10nService: L10nService,
        private readonly licensingPageStore: LicensingPageStore,
        private readonly notificationService: NotificationService,
        private readonly store: Store,
        @Inject('systemConfigService')
        private readonly systemConfigService: SystemConfig,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * @override
     */
    public ngOnInit(): void {
        combineLatest([
            this.licensingPageStore.connectivityStatus$,
            this.licensingPageStore.registrationStatus$,
        ])
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(([
                connectivityStatus,
                registrationStatus,
            ]: [ALBServicesConnectivityStatus, ALBServicesRegistrationStatus]) => {
                const wasDisconnected = this.previousConnectivityStatus ===
                    ALBServicesConnectivityStatus.ALBSERVICES_DISCONNECTED;
                const isNowConnected = connectivityStatus ===
                    ALBServicesConnectivityStatus.ALBSERVICES_CONNECTED;
                const isDeregistered = registrationStatus ===
                    ALBServicesRegistrationStatus.ALBSERVICES_DEREGISTERED;

                // Auto-open modal on first transition from DISCONNECTED to CONNECTED
                // when still DEREGISTERED (first time only)
                if (wasDisconnected && isNowConnected && isDeregistered && !this.hasOpenedModalOnConnect) {
                    this.hasOpenedModalOnConnect = true;
                    this.onLicenseTierChangeRequest.emit();
                }

                this.aviCloudConnected = isNowConnected;
                this.previousConnectivityStatus = connectivityStatus;
            });

        // Subscribe to license tier changes to update UI when tier changes from licensing modal
        this.licenseTierSubscription = this.store
            .select(SystemInfoSelectors.selectDefaultLicenseTier)
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((tier: LicenseTierType) => {
                this.licensingPageStore.setSelectedLicensingTier(tier);
            });

        this.licensingPageStore.tierInUse$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((tier: LicenseTierType) => {
                this.selectedTier = tier;
            });

        this.licensingPageStore.tierChangeInProgress$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((isTierChangeInProgress: boolean) => {
                this.isTierChangeInProgress = isTierChangeInProgress;
            });
    }

    /**
     * @override
     */
    public ngOnDestroy(): void {
        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();

        if (this.licenseTierSubscription) {
            this.licenseTierSubscription.unsubscribe();
        }
    }

    /**
     * Handle licensing type change (radio button selection).
     */
    public async onLicensingTypeChange(licensingType: LicenseTierType): Promise<void> {
        const validTypes = [LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES, LicenseTierType.ENTERPRISE];

        if (!validTypes.includes(licensingType)) {
            return;
        }

        // Only proceed if tier is actually changing
        if (this.selectedTier === licensingType) {
            return;
        }

        this.licensingPageStore.setSelectedLicensingTier(licensingType);
        this.busy = true;
        this.licensingPageStore.setTierChangeInProgress(true);

        try {
            this.systemConfigService.defaultLicenseTier = licensingType;
            this.licensingPageStore.setSelectedLicensingTier(licensingType);

            await this.systemConfigService.saveDefaultLicenseTierType();

            if (licensingType === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES ||
                this.selectedTier === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES) {
                await this.licensingPageStore.loadConnectivityStatus();
            }

            this.store.dispatch(SystemInfoActions.passiveLoadSystemInfo());
            this.licenseTierSwitchNotification(licensingType);
        } catch (error) {
            const errorObj = error as { error?: string };
            const errorMessage = errorObj?.error ||
                this.l10nService.getMessage(l10nKeys.defaultTierSwitchErrorMessage);

            this.onError.emit(errorMessage);
        } finally {
            this.busy = false;
            this.licensingPageStore.setTierChangeInProgress(false);
        }
    }

    /**
     * Authenticate user with the portal similar to connectAndRegisterController.
     */
    public handleConnectToCloudLicensing(): IPromise<string | void> {
        if (this.isTierChangeInProgress) {
            return Promise.resolve();
        }

        this.busy = true;

        return this.connectAviCloud()
            .then(async() => {
                await this.licensingPageStore.loadConnectivityStatus();
                await this.licensingPageStore.loadLicenseStatus();

                if (!this.aviCloudConnected) {
                    this.error = this.l10nService.getMessage(l10nKeys.licensingConnectivityError);
                }
            })
            .then(() => {
                // Emit event to parent to show licensing status card after successful authentication
                this.onConnectToCloudLicensing.emit({ licensingType: LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES });
            })
            .catch(error => {
                const errorObj = error as { error?: string };
                const errorMessage = errorObj?.error ||
                    this.l10nService.getMessage(l10nKeys.failedToConnectCloudLicensingError);

                this.error = errorMessage;
                this.onError.emit(errorMessage);
            })
            .finally(() => this.busy = false);
    }

    /**
     * Function to authenticate user with Portal.
     */
    private connectAviCloud(): IPromise<void> {
        return this.aviCloudConnected ? Promise.resolve() : this.cPortalService.openLoginWindow();
    }

    /**
     * Handle Use On-prem License Hub button click.
     */
    public handleUseOnPremLicensingService(): void {
        if (this.isTierChangeInProgress) {
            return;
        }

        this.dialogService.add({
            id: CONNECT_TO_ON_PREM_MODAL,
            component: ConnectToOnPremModalComponent as Type<Component>,
            componentProps: {
                onClose: () => {
                    this.dialogService.remove(CONNECT_TO_ON_PREM_MODAL);
                },
            },
        });
    }

    /**
     * Handle show success notification on licensing type change (radio button selection).
     * Show success notification with Edit Settings link
     */
    private licenseTierSwitchNotification(licensingType: string): void {
        const OPEN_LICENSE_TIER_CHANGE_MODAL = 'OPEN_LICENSE_TIER_CHANGE_MODAL';

        if (this.notificationService.has(OPEN_LICENSE_TIER_CHANGE_MODAL)) {
            this.notificationService.remove(OPEN_LICENSE_TIER_CHANGE_MODAL);
        }

        const licensingTypeLabel = licensingType === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES ?
            this.l10nService.getMessage(l10nKeys.cloudLicensingLabel) :
            this.l10nService.getMessage(l10nKeys.onPremLicensingServiceLabel);

        const openLicenseTierModal = (): void => {
            this.onLicenseTierChangeRequest.emit();
        };

        this.notificationService.add({
            id: OPEN_LICENSE_TIER_CHANGE_MODAL,
            component: LicenseTierSwitchNotificationComponent as Type<Component>,
            componentProps: {
                licensingTypeLabel,
                onEditSettingsClick: openLicenseTierModal,
            },
        });
    }
}
